import React from 'react';
import { 
    Box, 
    Container, 
    Typography, 
    Grid, 
    Card, 
    CardContent, 
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Alert
} from '@mui/material';
import {
    Timeline,
    TimelineItem,
    TimelineSeparator,
    TimelineConnector,
    TimelineContent,
    TimelineDot
} from '@mui/lab';
import { styled } from '@mui/material/styles';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import BlockIcon from '@mui/icons-material/Block';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import PaymentIcon from '@mui/icons-material/Payment';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EmailIcon from '@mui/icons-material/Email';
import WarningIcon from '@mui/icons-material/Warning';

// Styled components
const StyledHero = styled(Box)(({ theme }) => ({
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: theme.spacing(8, 0),
    textAlign: 'center',
    color: 'white',
}));

const StyledSection = styled(Box)(({ theme }) => ({
    padding: theme.spacing(6, 0),
}));

const PolicyCard = styled(Card)(({ theme }) => ({
    height: '100%',
    borderRadius: '16px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
    transition: 'transform 0.3s ease-in-out',
    border: '2px solid transparent',
    '&:hover': {
        transform: 'translateY(-4px)',
        borderColor: theme.palette.primary.light,
    },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
    background: 'linear-gradient(45deg, #667eea, #764ba2)',
    color: 'white',
    margin: '0 auto',
}));

const ProcessCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(3),
    borderRadius: '12px',
    textAlign: 'center',
    border: '2px solid',
    borderColor: theme.palette.primary.light,
    backgroundColor: theme.palette.primary.lighter,
}));

export default function Cancellation() {
    const policyData = [
        {
            icon: <AssignmentReturnIcon />,
            title: "Return Eligibility",
            color: "success",
            items: [
                "Products must be unused, unopened, and in their original packaging",
                "Returns must be initiated within 7 days of delivery",
                "Items must be in resaleable condition"
            ],
            timeframe: "7 days"
        },
        {
            icon: <BlockIcon />,
            title: "Non-Returnable Items",
            color: "error",
            items: [
                "Personal care items like opened skincare products are non-returnable",
                "Hygiene and safety reasons apply",
                "Once opened, products cannot be returned"
            ],
            timeframe: "Not applicable"
        },
        {
            icon: <SupportAgentIcon />,
            title: "Return Process",
            color: "info",
            items: [
                "Contact us at info@glamourradiance.com with your order details",
                "Our team will guide you through the return process",
                "Follow the provided return instructions carefully"
            ],
            timeframe: "Immediate"
        },
        {
            icon: <PaymentIcon />,
            title: "Refunds",
            color: "primary",
            items: [
                "Refunds are processed within 7-10 business days",
                "Processing starts after we receive and inspect the returned product",
                "Refunds will be issued to the original payment method"
            ],
            timeframe: "7-10 business days"
        },
        {
            icon: <SwapHorizIcon />,
            title: "Exchanges",
            color: "warning",
            items: [
                "Exchanges are subject to product availability",
                "If the desired product is unavailable, a refund will be provided",
                "Same terms apply as returns"
            ],
            timeframe: "Subject to availability"
        },
        {
            icon: <ReportProblemIcon />,
            title: "Damaged or Incorrect Items",
            color: "error",
            items: [
                "Contact us within 48 hours of delivery",
                "Report damaged or incorrect products immediately",
                "Replacement or refund will be provided"
            ],
            timeframe: "48 hours"
        },
        {
            icon: <CancelIcon />,
            title: "Cancellation Policy",
            color: "secondary",
            items: [
                "Orders can be canceled within 24 hours of placing them",
                "After 24 hours, cancellations are subject to approval",
                "Contact customer support for cancellation requests"
            ],
            timeframe: "24 hours"
        }
    ];

    const returnProcess = [
        {
            title: "Contact Us",
            description: "Email us at info@glamourradiance.com with your order details",
            icon: <EmailIcon />
        },
        {
            title: "Get Instructions",
            description: "Our team will provide return instructions and process details",
            icon: <SupportAgentIcon />
        },
        {
            title: "Ship Product",
            description: "Package and ship the product back to us as instructed",
            icon: <AssignmentReturnIcon />
        },
        {
            title: "Inspection",
            description: "We inspect the returned product for eligibility",
            icon: <CheckCircleIcon />
        },
        {
            title: "Refund/Exchange",
            description: "Process refund or exchange within 7-10 business days",
            icon: <PaymentIcon />
        }
    ];

    return (
        <Box>
            {/* Hero Section */}
            <StyledHero>
                <Container maxWidth="lg">
                    <AssignmentReturnIcon sx={{ fontSize: '4rem', mb: 2, opacity: 0.9 }} />
                    <Typography 
                        variant="h2" 
                        component="h1" 
                        gutterBottom 
                        sx={{ 
                            fontWeight: 'bold',
                            textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
                        }}
                    >
                        Returns & Cancellation Policy
                    </Typography>
                    <Typography 
                        variant="h5" 
                        sx={{ 
                            maxWidth: '800px', 
                            mx: 'auto',
                            lineHeight: 1.6,
                            fontWeight: 300,
                            opacity: 0.95
                        }}
                    >
                        Your satisfaction is our priority. Learn about our return, exchange, 
                        and cancellation policies designed with your convenience in mind.
                    </Typography>
                </Container>
            </StyledHero>

            {/* Quick Overview */}
            <StyledSection sx={{ backgroundColor: 'grey.50' }}>
                <Container maxWidth="lg">
                    <Typography 
                        variant="h4" 
                        component="h2" 
                        gutterBottom 
                        sx={{ 
                            textAlign: 'center',
                            color: 'primary.main',
                            fontWeight: 600,
                            mb: 4
                        }}
                    >
                        Quick Overview
                    </Typography>
                    
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} md={3}>
                            <ProcessCard>
                                <AccessTimeIcon sx={{ fontSize: '2rem', color: 'success.main', mb: 1 }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>
                                    7 Days
                                </Typography>
                                <Typography variant="body2">
                                    Return Window
                                </Typography>
                            </ProcessCard>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <ProcessCard>
                                <WarningIcon sx={{ fontSize: '2rem', color: 'error.main', mb: 1 }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, color: 'error.main' }}>
                                    48 Hours
                                </Typography>
                                <Typography variant="body2">
                                    Report Damaged Items
                                </Typography>
                            </ProcessCard>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <ProcessCard>
                                <CancelIcon sx={{ fontSize: '2rem', color: 'warning.main', mb: 1 }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, color: 'warning.main' }}>
                                    24 Hours
                                </Typography>
                                <Typography variant="body2">
                                    Cancellation Window
                                </Typography>
                            </ProcessCard>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <ProcessCard>
                                <PaymentIcon sx={{ fontSize: '2rem', color: 'info.main', mb: 1 }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, color: 'info.main' }}>
                                    7-10 Days
                                </Typography>
                                <Typography variant="body2">
                                    Refund Processing
                                </Typography>
                            </ProcessCard>
                        </Grid>
                    </Grid>
                </Container>
            </StyledSection>

            {/* Policy Details */}
            <StyledSection>
                <Container maxWidth="lg">
                    <Typography 
                        variant="h4" 
                        component="h2" 
                        gutterBottom 
                        sx={{ 
                            textAlign: 'center',
                            color: 'primary.main',
                            fontWeight: 600,
                            mb: 6
                        }}
                    >
                        Detailed Policy Information
                    </Typography>
                    
                    <Grid container spacing={4}>
                        {policyData.map((policy, index) => (
                            <Grid item xs={12} md={6} lg={4} key={index}>
                                <PolicyCard>
                                    <CardContent sx={{ p: 4 }}>
                                        <Box sx={{ textAlign: 'center', mb: 3 }}>
                                            <IconWrapper>
                                                {policy.icon}
                                            </IconWrapper>
                                            <Typography 
                                                variant="h6" 
                                                component="h3" 
                                                gutterBottom 
                                                sx={{ 
                                                    color: 'primary.main',
                                                    fontWeight: 600
                                                }}
                                            >
                                                {policy.title}
                                            </Typography>
                                            <Chip 
                                                label={policy.timeframe}
                                                color={policy.color}
                                                size="small"
                                                sx={{ mb: 2 }}
                                            />
                                        </Box>
                                        
                                        <List sx={{ p: 0 }}>
                                            {policy.items.map((item, itemIndex) => (
                                                <ListItem key={itemIndex} sx={{ px: 0, py: 1 }}>
                                                    <ListItemIcon sx={{ minWidth: '24px' }}>
                                                        <Box 
                                                            sx={{ 
                                                                width: '8px', 
                                                                height: '8px', 
                                                                borderRadius: '50%', 
                                                                backgroundColor: `${policy.color}.main` 
                                                            }} 
                                                        />
                                                    </ListItemIcon>
                                                    <ListItemText 
                                                        primary={item} 
                                                        sx={{ 
                                                            '& .MuiListItemText-primary': { 
                                                                fontSize: '0.9rem',
                                                                lineHeight: 1.5,
                                                                color: 'text.primary'
                                                            } 
                                                        }}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </CardContent>
                                </PolicyCard>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </StyledSection>

            <Divider />

            {/* Return Process Timeline */}
            <StyledSection sx={{ backgroundColor: 'grey.50' }}>
                <Container maxWidth="md">
                    <Typography 
                        variant="h4" 
                        component="h2" 
                        gutterBottom 
                        sx={{ 
                            textAlign: 'center',
                            color: 'primary.main',
                            fontWeight: 600,
                            mb: 6
                        }}
                    >
                        Return Process Timeline
                    </Typography>

                    <Timeline position="alternate">
                        {returnProcess.map((step, index) => (
                            <TimelineItem key={index}>
                                <TimelineSeparator>
                                    <TimelineDot 
                                        sx={{ 
                                            backgroundColor: 'primary.main',
                                            color: 'white',
                                            width: '60px',
                                            height: '60px'
                                        }}
                                    >
                                        {step.icon}
                                    </TimelineDot>
                                    {index < returnProcess.length - 1 && <TimelineConnector />}
                                </TimelineSeparator>
                                <TimelineContent sx={{ py: '12px', px: 2 }}>
                                    <Card sx={{ p: 2, boxShadow: 2 }}>
                                        <Typography variant="h6" component="h3" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                            Step {index + 1}: {step.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {step.description}
                                        </Typography>
                                    </Card>
                                </TimelineContent>
                            </TimelineItem>
                        ))}
                    </Timeline>
                </Container>
            </StyledSection>

            {/* Important Notices */}
            <StyledSection>
                <Container maxWidth="lg">
                    <Typography 
                        variant="h4" 
                        component="h2" 
                        gutterBottom 
                        sx={{ 
                            textAlign: 'center',
                            color: 'primary.main',
                            fontWeight: 600,
                            mb: 4
                        }}
                    >
                        Important Notices
                    </Typography>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Alert 
                                severity="warning" 
                                sx={{ 
                                    borderRadius: '12px',
                                    '& .MuiAlert-message': { fontSize: '1rem' }
                                }}
                            >
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                    Hygiene Policy
                                </Typography>
                                Opened personal care and skincare products cannot be returned for hygiene 
                                and safety reasons. Please ensure you're satisfied before opening.
                            </Alert>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                            <Alert 
                                severity="info" 
                                sx={{ 
                                    borderRadius: '12px',
                                    '& .MuiAlert-message': { fontSize: '1rem' }
                                }}
                            >
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                    Refund Timeline
                                </Typography>
                                Refunds typically take 7-10 business days to process after we receive 
                                your return. Bank processing may take additional time.
                            </Alert>
                        </Grid>
                    </Grid>
                </Container>
            </StyledSection>

            {/* Contact Section */}
            <StyledSection sx={{ backgroundColor: 'primary.50' }}>
                <Container maxWidth="md">
                    <Box 
                        sx={{ 
                            textAlign: 'center',
                            p: 6,
                            backgroundColor: 'white',
                            borderRadius: '24px',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                        }}
                    >
                        <SupportAgentIcon 
                            sx={{ 
                                fontSize: '4rem',
                                color: 'primary.main',
                                mb: 2
                            }} 
                        />
                        <Typography 
                            variant="h4" 
                            component="h3" 
                            gutterBottom 
                            sx={{ 
                                color: 'primary.main',
                                fontWeight: 600,
                                mb: 2
                            }}
                        >
                            Need Help with Returns?
                        </Typography>
                        <Typography 
                            variant="body1" 
                            color="text.secondary" 
                            sx={{ 
                                fontSize: '1.1rem',
                                lineHeight: 1.6,
                                mb: 3
                            }}
                        >
                            Our customer support team is here to assist you with any return, 
                            exchange, or cancellation requests. Contact us for personalized assistance.
                        </Typography>
                        <Box 
                            sx={{ 
                                display: 'flex', 
                                flexDirection: { xs: 'column', sm: 'row' }, 
                                gap: 2, 
                                justifyContent: 'center',
                                alignItems: 'center'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <EmailIcon sx={{ color: 'primary.main' }} />
                                <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                                    info@glamourradiance.com
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Container>
            </StyledSection>
        </Box>
    );
}